import React, { Fragment, useEffect, useState } from 'react';
import { Button, Row, Col, Form } from 'react-bootstrap';

import {
  requestList as request,
  requestImport
} from 'api/standard/code/product';
import { requestList as requestCode } from 'api/standard/code';
import ActionButton from 'components/common/ActionButton';
import CheckPlant from 'components/common-sfims/CheckPlant';
import Search from 'components/common-sfims/form/Search';
import Select from 'components/common-sfims/form/Select';
import ListInContent from 'components/common-sfims/ListInContent';
import columns from 'components/pages/standard/code/product/columns';
import Setting from 'components/pages/standard/code/product/Setting';
import Upload from 'components/pages/standard/code/product/Upload';
import { array2ValueAndText, datas2ExcelJSON, utc2tz } from 'function/convert';
import { downloadExcelByJSON } from 'function/file';
import { getText } from 'function/language';
import { getLoginInform } from 'function/login';
import { getErrorMessage } from 'function/message';
import useCheckPlantState from 'global/checkPlantState';
import { useConfirmModalState } from 'global/confirmModalState';
import usePlantState from 'global/plantState';
import useListScroll from 'hooks/useListScroll';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE, KOREA_BU } from 'types/const';
import { RETURN_YN, RETURN_YN_SELECT } from 'types/type';
import comma from 'utils/format/comma';
import format from 'utils/string/format';

/**
 * 기준 정보 > 제품 코드 - 목록
 */
const List = () => {
  /* variable */
  const { adminYn, fatoryCodeSeq } = getLoginInform();

  /* hook */
  const { end, setEnd, total, setTotal } = useListScroll();

  /* recoil */
  const { checkPlant } = useCheckPlantState();
  const { setConfirmModal } = useConfirmModalState();
  const {
    plant: { hasReads: hasReadPlants }
  } = usePlantState();

  /* state */
  // 목록
  const [datas, setDatas] = useState([]);
  // 선택한 성형
  const [feedFormCd, setFeedFormCd] = useState();
  // 성형 목록
  const [feedFormCds, setFeedFormCds] = useState([]);
  // SAP에서 가져오기 비활성화 여부
  const [isDisableImport, setIsDisableImport] = useState(false);
  // 해외 공장 여부
  const [isForeignPlant, setIsForeignPlant] = useState(false);
  // 로딩 여부
  const [isLoading, setIsLoading] = useState(true);
  // 설정 모달 표시 여부
  const [isShowSetting, setIsShowSetting] = useState(false);
  // 업로드 모달 표시 여부
  const [isShowUpload, setIsShowUpload] = useState(false);
  // 메시지
  const [message, setMessage] = useState({});
  // 선택한 축종
  const [mtrilGrpCd, setMtrilGrpCd] = useState();
  // 축종 목록
  const [mtrilGrpCds, setMtrilGrpCds] = useState([]);
  // 선택한 포장 형태
  const [packageUnitCd, setPackageUnitCd] = useState();
  // 포장 형태 목록
  const [packageUnitCds, setPackageUnitCds] = useState([]);
  // 제품 코드 목록 조회 요청 데이터
  const [params, setParams] = useObjReducer({ factoryCodeSeqList: checkPlant });
  // 제품 코드 검색어
  const [prdtCd, setPrdtCd] = useState('');
  // 선택한 판매 단위
  const [salesKgCd, setSalesKgCd] = useState();
  // 판매 단위 목록
  const [salesKgCds, setSalesKgCds] = useState([]);
  // 선택한 번호
  const [selectSeq, setSelectSeq] = useState(0);
  // 사육 단계 검색어
  const [sunjinStageCd, setSunjinStageCd] = useState('');
  // 사육 단계 목록
  const [sunjinStageCds, setSunjinStageCds] = useState([]);
  // 선택한 사용 유무
  const [useYn, setUseYn] = useState();

  /* watch */
  useEffect(() => {
    requestMtrilGrpCd();
    requestSunjinStageCd();
    requestFeedFormCd();
    requestPackageUnitCd();
    requestSalesKgCd();
  }, []);
  useEffect(() => {
    setIsForeignPlant(
      hasReadPlants.find(({ seq } = {}) => seq === fatoryCodeSeq)?.buCode !==
        KOREA_BU
    );
  }, [hasReadPlants]);
  useEffect(() => {
    requestList();
  }, [params]);

  /* method */
  // 성형 목록 조회 요청
  const requestFeedFormCd = async () => {
    const { list = [] } = await requestCode({ codeGroup: 'feed_form' });

    setFeedFormCds(list);
  };
  // 제품 코드 목록 조회 요청
  const requestList = async () => {
    setEnd(20);
    setTotal(0);
    setDatas([]);
    setIsLoading(true);
    setMessage({});

    try {
      const {
        list = [],
        totalCount = 0,
        code,
        message
      } = await request(params);

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      setTotal(totalCount);
      setDatas(
        list.map((value = {}, index) => ({
          ...value,
          rowNum: totalCount - index,
          regDt: utc2tz(value.regDt)
        }))
      );
    } catch (error) {
      setMessage(error);
    } finally {
      setIsLoading(false);
    }
  };
  // 축종 목록 조회 요청
  const requestMtrilGrpCd = async () => {
    const { list = [] } = await requestCode({ codeGroup: 'species' });

    setMtrilGrpCds(list);
  };
  // 포장 형태 목록 조회 요청
  const requestPackageUnitCd = async () => {
    const { list = [] } = await requestCode({ codeGroup: 'package_unit' });

    setPackageUnitCds(list);
  };
  // 판매 단위 목록 조회 요청
  const requestSalesKgCd = async () => {
    const { list = [] } = await requestCode({ codeGroup: 'sales_kg' });

    setSalesKgCds(list);
  };
  // 사육 단계 목록 조회 요청
  const requestSunjinStageCd = async () => {
    const { list = [] } = await requestCode({ codeGroup: 'sunjin_stage' });

    setSunjinStageCds(list);
  };
  // 설정 모달 표시
  const showSetting = seq => {
    setIsShowSetting(true);
    setSelectSeq(seq);
  };

  /* event */
  // 엑셀 다운로드 클릭 시
  const onDownloadExcelClick = () => {
    downloadExcelByJSON(
      datas2ExcelJSON(
        datas,
        Object.fromEntries(
          Object.entries(columns).filter(([key]) => key !== 'update')
        )
      ),
      `${getText('prdtCd')}.xlsx`
    );
  };
  // SAP에서 가져오기 클릭 시
  const onImportClick = () => {
    setConfirmModal({
      isOpen: true,
      title: 'importSAP',
      message: 'importSAPMessage',
      isCancelBtn: true,
      btnNm: 'import',
      async onConfirm() {
        let title = 'complete';
        let message = 'completeImport';

        setIsDisableImport(true);

        try {
          const { code, message } = await requestImport();

          if (code !== RESPONSE_CODE.Success) {
            throw {
              code,
              error: message
            };
          }
        } catch (error) {
          title = 'notice';
          message = getErrorMessage(error?.code, error?.error);
        } finally {
          setConfirmModal({
            isOpen: true,
            title,
            message
          });
          setIsDisableImport(false);
        }
      }
    });
  };
  // 검색 클릭 시
  const onSearchClick = event => {
    event.preventDefault();

    setParams({
      factoryCodeSeqList: checkPlant,
      prdtCd,
      mtrilGrpCd,
      sunjinStageCd: sunjinStageCds.find(
        ({ codeNmKr } = {}) => codeNmKr === sunjinStageCd
      )?.codeCd,
      feedFormCd,
      packageUnitCd,
      salesKgCd,
      useYn
    });
  };

  /* view */
  return (
    <>
      <ListInContent
        columns={Object.entries(columns).map(
          ([key, { title } = {}]) => title ?? key
        )}
        message={message}
        showLoading={isLoading}
      >
        <Fragment key="headerButton">
          {/* {(adminYn === RETURN_YN.Y || !isForeignPlant) && (
            <Button
              disabled={isDisableImport}
              size="sm"
              onClick={onImportClick}
            >
              {getText('importSAP')}
            </Button>
          )} */}
          {(adminYn === RETURN_YN.Y || isForeignPlant) && (
            <>
              <Button
                size="sm"
                className="ms-2"
                onClick={() => setIsShowUpload(true)}
              >
                {getText('uploadExcel')}
              </Button>
              <Button size="sm" className="ms-2" onClick={() => showSetting(0)}>
                + {getText('insert')}
              </Button>
            </>
          )}
        </Fragment>
        <Fragment key="search">
          <CheckPlant />
          <Form onSubmit={onSearchClick}>
            <Row className="ms-0 me-0 mt-3">
              <Col sm={1} className="ps-1 pe-1">
                <Form.Control
                  size="sm"
                  type="text"
                  value={prdtCd}
                  autoFocus
                  placeholder={getText('prdtCd')}
                  onChange={({ target: { value } }) => setPrdtCd(value)}
                />
              </Col>
              <Col sm={1} className="ps-1 pe-1">
                <Select
                  datas={array2ValueAndText(mtrilGrpCds, 'codeCd', 'codeNmKr')}
                  placeholder="species"
                  showAll
                  value={mtrilGrpCd}
                  className="w-100"
                  onChange={({ target: { value } }) => setMtrilGrpCd(value)}
                />
              </Col>
              <Col sm={2} className="ps-1 pe-1">
                <Search
                  datas={array2ValueAndText(sunjinStageCds, 'codeNmKr')}
                  placeholder="sunjinStageNm"
                  value={sunjinStageCd}
                  setValue={setSunjinStageCd}
                />
              </Col>
              <Col sm={2} className="ps-1 pe-1">
                <Select
                  datas={array2ValueAndText(feedFormCds, 'codeCd', 'codeNmKr')}
                  placeholder="feedFormNm"
                  showAll
                  value={feedFormCd}
                  className="w-100"
                  onChange={({ target: { value } }) => setFeedFormCd(value)}
                />
              </Col>
              <Col sm={1} className="ps-1 pe-1">
                <Select
                  datas={array2ValueAndText(
                    packageUnitCds,
                    'codeCd',
                    'codeNmKr'
                  )}
                  placeholder="packageUnitNm"
                  showAll
                  value={packageUnitCd}
                  className="w-100"
                  onChange={({ target: { value } }) => setPackageUnitCd(value)}
                />
              </Col>
              <Col sm={1} className="ps-1 pe-1">
                <Select
                  datas={array2ValueAndText(salesKgCds, 'codeCd', 'codeNmKr')}
                  placeholder="salesKgNm"
                  showAll
                  value={salesKgCd}
                  className="w-100"
                  onChange={({ target: { value } }) => setSalesKgCd(value)}
                />
              </Col>
              <Col sm={1} className="ps-1 pe-1">
                <Select
                  datas={array2ValueAndText(RETURN_YN_SELECT, 'key', 'value')}
                  placeholder="useYn"
                  showAll
                  value={useYn}
                  className="w-100"
                  onChange={({ target: { value } }) => setUseYn(value)}
                />
              </Col>
              <Col sm={3} className="ps-1 pe-1 d-flex align-items-center">
                <Button disabled={isLoading} size="sm" type="submit">
                  {getText('search')}
                </Button>
                <p className="mb-0 ms-3 fs--1 fw-bold lh-1">
                  {format(getText('total'), { total: comma(total) })}
                </p>
                {datas.length > 0 && (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="ms-2"
                    onClick={onDownloadExcelClick}
                  >
                    {getText('downloadExcel')}
                  </Button>
                )}
              </Col>
            </Row>
          </Form>
        </Fragment>
        <Fragment key="datas">
          {datas.slice(0, end).map((value = {}) => (
            <tr
              key={`datas${value.seq}`}
              className={
                value.useYn === RETURN_YN.Y ? 'bg-soft-success' : 'bg-200'
              }
            >
              {Object.keys(columns).map(key => (
                <td key={`datas${value.seq}.${key}`}>
                  {key === 'update' ? (
                    <ActionButton
                      title={getText('update')}
                      icon="edit"
                      variant="action"
                      className="p-0 lh-1"
                      onClick={() => showSetting(value.seq)}
                    />
                  ) : (
                    value[key]
                  )}
                </td>
              ))}
            </tr>
          ))}
        </Fragment>
      </ListInContent>
      <Upload
        show={isShowUpload}
        onHide={() => setIsShowUpload(false)}
        onUpload={requestList}
      />
      <Setting
        show={isShowSetting}
        feedFormCds={feedFormCds}
        mtrilGrpCds={mtrilGrpCds}
        packageUnitCds={packageUnitCds}
        salesKgCds={salesKgCds}
        seq={selectSeq}
        sunjinStageCds={sunjinStageCds}
        onHide={() => setIsShowSetting(false)}
        onSetting={requestList}
      />
    </>
  );
};

export default List;
